import React, { useState, useEffect, useCallback } from 'react';
import { List, Avatar, Tag, Button, Typography, Space } from 'antd';
import { PlusOutlined, UserOutlined, ReloadOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { Role } from '../constants/roles';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import DataCard from '../components/page/DataCard';
import InviteModal from '../components/InviteModal';
import { displayEmail } from '../utils/displayEmail';

const { Text } = Typography;

const ROLE_LABELS = {
    lead_advisor: 'Lead Project Advisor',
    advisor: 'Advisor',
    company_spoc: 'Company SPOC',
    company_delegate: 'Company Delegate',
    platform_admin: 'Platform Admin',
};

const ROLE_COLORS = {
    lead_advisor: '#052873',
    advisor: '#7c3aed',
    company_spoc: '#06b6d4',
    company_delegate: '#f97316',
    platform_admin: 'var(--color-text-muted)',
};

const getInvitableRoles = (userRole) => {
    switch (userRole) {
        case Role.ADMIN:
            return [{ value: 'lead_advisor', label: 'Lead Advisor' }];
        case Role.LEAD_ADVISOR:
            return [
                { value: 'advisor', label: 'Advisor' },
                { value: 'company_spoc', label: 'Company SPOC' },
            ];
        case Role.COMPANY_SPOC:
        // QE-04: workflow slide 7 (Project onboarding) - Advisor adds company delegates to the project.
        case Role.ADVISOR:
            return [{ value: 'company_delegate', label: 'Company Delegate' }];
        default:
            return [];
    }
};

const ProjectMembers = () => {
    const { user, activeProject, fetchProjectMembers } = useApp();
    const [showInvite, setShowInvite] = useState(false);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadMembers = useCallback(async () => {
        if (!activeProject?.id) return;
        setLoading(true);
        const data = await fetchProjectMembers(activeProject.id);
        setMembers(data);
        setLoading(false);
    }, [activeProject?.id, fetchProjectMembers]);

    useEffect(() => {
        loadMembers();
    }, [loadMembers]);

    if (!activeProject) {
        return <PageState status="empty" message="No project selected" />;
    }

    const invitableRoles = getInvitableRoles(user?.role);
    const canInvite = invitableRoles.length > 0;

    const displayMembers = members.map((m) => ({
        id: m.id,
        name: m.name,
        role: ROLE_LABELS[m.role] || m.role?.replace(/_/g, ' ') || 'Member',
        rawRole: m.role,
        email: m.email,
        isLead: m.role === 'lead_advisor',
    }));

    return (
        <PageShell>
            <PageHeader
                title="Project Team"
                subtitle={<>Managing access for <Text strong>{activeProject.name}</Text></>}
                actions={canInvite ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowInvite(true)}>
                        Invite Team Member
                    </Button>
                ) : null}
            />

            <DataCard
                title={
                    <Space>
                        <Text strong>Authorized Registry</Text>
                        <Tag style={{ fontWeight: 700, fontSize: 11 }}>
                            {loading ? '...' : `${displayMembers.length} IDENTITIES ASSIGNED`}
                        </Tag>
                    </Space>
                }
            >
                {loading ? (
                    <PageState status="loading" />
                ) : displayMembers.length === 0 ? (
                    <PageState status="empty" message="No team members assigned yet. Invite someone to get started." />
                ) : (
                    <List
                        dataSource={displayMembers}
                        renderItem={(member) => (
                            <List.Item
                                extra={
                                    member.isLead && (
                                        <Tag color="blue" style={{ fontWeight: 700, fontSize: 11 }}>Lead Entity</Tag>
                                    )
                                }
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar
                                            style={{
                                                backgroundColor: ROLE_COLORS[member.rawRole] || 'var(--color-text-muted)',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                                        </Avatar>
                                    }
                                    title={<Text strong>{member.name}</Text>}
                                    description={
                                        <span style={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }}>
                                            {member.role} &bull; {displayEmail(member.email)}
                                        </span>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                )}
            </DataCard>

            {showInvite && (
                <InviteModal
                    onClose={() => setShowInvite(false)}
                    onInviteSuccess={loadMembers}
                    roles={invitableRoles}
                    projectName={activeProject.name}
                />
            )}
        </PageShell>
    );
};

export default ProjectMembers;
